// Pie del portal: datos oficiales de atención de la Alcaldía de Tunja
// (fuente: tunja-boyaca.gov.co), enlaces y logos institucionales.
import { Box, Container, Divider, IconButton, Link as MuiLink, Typography } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import XIcon from "@mui/icons-material/X";
import YouTubeIcon from "@mui/icons-material/YouTube";
import logoAlcaldia from "../../../activos/imagenes/nav/Logo_Alcaldía_Mayor_de_Tunja.png";
import logoGovco from "../../../activos/imagenes/nav/gov.com.co.png";
import logoMintic from "../../../activos/imagenes/nav/logo_mintic.svg";
import { useIdioma } from "../idioma/IdiomaContext";

const AZUL_GOVCO = "#3366CC";

const Dato = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>
      {titulo}
    </Typography>
    <Typography sx={{ fontSize: "0.92rem" }}>{children}</Typography>
  </Box>
);

const TituloColumna = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontWeight: 800, mb: 2 }}>{children}</Typography>
);

export default function Footer() {
  const { t } = useIdioma();
  const anio = new Date().getFullYear();
  const redes = [
    { icono: <FacebookIcon />, url: "https://www.facebook.com/AlcaldiaTunja/", nombre: "Facebook" },
    { icono: <XIcon />, url: "https://twitter.com/AlcaldiaTunja", nombre: "X" },
    { icono: <InstagramIcon />, url: "https://www.instagram.com/alcaldiadetunja/", nombre: "Instagram" },
    { icono: <YouTubeIcon />, url: "https://www.youtube.com/user/PrensaAlcaldiaTunja", nombre: "YouTube" },
  ];

  return (
    <Box component="footer" sx={{ bgcolor: "background.paper", borderTop: "1px solid", borderColor: "divider" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 1.4fr 1fr 0.8fr" }, gap: { xs: 4, md: 6 } }}>
          <Box>
            <Box component="img" src={logoAlcaldia} alt="Alcaldía Mayor de Tunja" sx={(th) => ({ height: 56, width: "auto", ...(th.palette.mode === "dark" && { bgcolor: "#fff", borderRadius: 2, px: 1, py: 0.5 }) })} />
            <Typography sx={{ color: "text.secondary", mt: 2, fontSize: "0.92rem" }}>{t.pie.descripcion}</Typography>
            <Box sx={{ display: "flex", gap: 0.5, mt: 2, ml: -1 }}>
              {redes.map((r) => (
                <IconButton key={r.nombre} href={r.url} target="_blank" rel="noopener noreferrer" aria-label={r.nombre} sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}>
                  {r.icono}
                </IconButton>
              ))}
            </Box>
          </Box>

          <Box>
            <TituloColumna>{t.pie.atencion}</TituloColumna>
            <Dato titulo={t.pie.direccion}>{t.pie.direccionValor}</Dato>
            <Dato titulo={t.pie.horario}>{t.pie.horarioValor}</Dato>
          </Box>

          <Box>
            <TituloColumna>{t.pie.contacto}</TituloColumna>
            <Dato titulo={t.pie.conmutador}>+57 (608) 740 5770</Dato>
            <Dato titulo={t.pie.lineaGratuita}>+57 (608) 740 5770 ext. 1709 – 1713</Dato>
            <Dato titulo={t.pie.correo}>
              <MuiLink href="mailto:atencionalciudadano@tunja.gov.co">atencionalciudadano@tunja.gov.co</MuiLink>
            </Dato>
            <Dato titulo={t.pie.notificaciones}>
              <MuiLink href="mailto:juridicaydefensa@tunja.gov.co">juridicaydefensa@tunja.gov.co</MuiLink>
            </Dato>
          </Box>

          <Box>
            <TituloColumna>{t.pie.enlaces}</TituloColumna>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, fontSize: "0.92rem" }}>
              <MuiLink href="https://www.tunja-boyaca.gov.co/" target="_blank" rel="noopener noreferrer" underline="hover">{t.pie.sitioAlcaldia}</MuiLink>
              <MuiLink href="https://www.gov.co/" target="_blank" rel="noopener noreferrer" underline="hover">{t.pie.portalEstado}</MuiLink>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", gap: 2 }}>
          <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>© {anio} {t.pie.derechos}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
            <Box sx={{ bgcolor: AZUL_GOVCO, px: 1.5, py: 0.75, borderRadius: 2, display: "flex" }}>
              <Box component="img" src={logoGovco} alt="GOV.CO" sx={{ height: 24, width: "auto" }} />
            </Box>
            <Box
              component="img"
              src={logoMintic}
              alt="Ministerio de Tecnologías de la Información y las Comunicaciones"
              sx={{ height: 44, width: "auto", filter: (th) => (th.palette.mode === "dark" ? "brightness(0) invert(1)" : "none") }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
